import axios from 'axios';
import authService from './authService';
import router from '@/router';
import { API_URL } from '@/config/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = authService.token.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Erro na requisição API:', error.message);

    // Só faz logout se a rota atual requer autenticação
    if (error.response?.status === 401) {
      const currentRoute = router.currentRoute;
      const requiresAuth = currentRoute.matched.some(record => record.meta.requiresAuth === true);

      if (requiresAuth) {
        authService.logout();
      }
    }

    return Promise.reject(error);
  }
);

class ApiService {
  constructor(baseEndpoint) {
    this.baseEndpoint = baseEndpoint;
  }

  async get(endpoint = '', params = {}) {
    try {
      const response = await api.get(`${this.baseEndpoint}${endpoint}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar dados de ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await api.post(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erro ao criar registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await api.put(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const response = await api.delete(`${this.baseEndpoint}${endpoint}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao excluir registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }
}

class PagamentoService extends ApiService {
  constructor() {
    super('/pagamentos');
  }

  static FORMA_PAGAMENTO_MAP = {
    'Dinheiro': 'DINHEIRO',
    'Cartão de Crédito': 'CARTAO_CREDITO',
    'Cartão de Débito': 'CARTAO_DEBITO',
    'PIX': 'PIX',
    'Boleto': 'BOLETO',
    'Transferência Bancária': 'TRANSFERENCIA_BANCARIA',
    'Cheque': 'CHEQUE',
    'Outro': 'OUTRO'
  };

  static FORMA_PAGAMENTO_DISPLAY = {
    'DINHEIRO': 'Dinheiro',
    'CARTAO_CREDITO': 'Cartão de Crédito',
    'CARTAO_DEBITO': 'Cartão de Débito',
    'PIX': 'PIX',
    'BOLETO': 'Boleto',
    'TRANSFERENCIA_BANCARIA': 'Transferência Bancária',
    'CHEQUE': 'Cheque',
    'OUTRO': 'Outro'
  };

  static STATUS_MAP = {
    'Pago': 'PAGO',
    'Pendente': 'PENDENTE',
    'Vencido': 'VENCIDO',
    'Cancelado': 'CANCELADO',
    'Parcial': 'PARCIAL',
    'Em Processamento': 'EM_PROCESSAMENTO'
  };

  static STATUS_DISPLAY = {
    'PAGO': 'Pago',
    'PENDENTE': 'Pendente',
    'VENCIDO': 'Vencido',
    'CANCELADO': 'Cancelado',
    'PARCIAL': 'Parcial',
    'EM_PROCESSAMENTO': 'Em Processamento'
  };

  prepareRequestDTO(pagamento) {
    return {
      dataPagamento: pagamento.dataPagamento,
      valor: pagamento.valor,
      descricao: pagamento.descricao,
      formaPagamento: PagamentoService.FORMA_PAGAMENTO_MAP[pagamento.formaPagamento],
      clienteId: pagamento.clienteId,
      status: pagamento.status ? PagamentoService.STATUS_MAP[pagamento.status] : 'PENDENTE'
    };
  }

  prepareResponseDTO(pagamento) {
    if (!pagamento) return null;

    return {
      id: pagamento.id,
      valor: pagamento.valor,
      dataPagamento: pagamento.dataPagamento,
      formaPagamento: PagamentoService.FORMA_PAGAMENTO_DISPLAY[pagamento.formaPagamento] || pagamento.formaPagamento,
      descricao: pagamento.descricao,
      clienteId: pagamento.clienteId,
      clienteNome: pagamento.clienteNome,
      status: PagamentoService.STATUS_DISPLAY[pagamento.status] || pagamento.status || 'Pendente'
    };
  }

  async getPagamentos() {
    try {
      const pagamentos = await this.get();
      return pagamentos.map(p => this.prepareResponseDTO(p));
    } catch (error) {
      console.error('Erro ao buscar todos os pagamentos:', error);
      return [];
    }
  }

  async getPagamentoPorId(id) {
    try {
      const pagamento = await this.get(`/${id}`);
      return this.prepareResponseDTO(pagamento);
    } catch (error) {
      console.error(`Erro ao buscar pagamento ${id}:`, error);
      return null;
    }
  }

  async getPagamentosPorCliente(clienteId) {
    try {
      const pagamentos = await this.get(`/cliente/${clienteId}`);
      return pagamentos.map(p => this.prepareResponseDTO(p));
    } catch (error) {
      console.error(`Erro ao buscar pagamentos do cliente ${clienteId}:`, error);
      return [];
    }
  }

  async getPagamentosPorPeriodo(dataInicio, dataFim) {
    try {
      const pagamentos = await this.get('/periodo', { inicio: dataInicio, fim: dataFim });
      return pagamentos.map(p => this.prepareResponseDTO(p));
    } catch (error) {
      console.error(`Erro ao buscar pagamentos por período:`, error);
      return [];
    }
  }

  async criarPagamento(pagamento) {
    try {
      const requestDTO = this.prepareRequestDTO(pagamento);
      return await this.post('', requestDTO);
    } catch (error) {
      console.error('Erro ao criar pagamento:', error);
      throw error;
    }
  }

  async criarPagamentoParcelado(pagamentoParcelado) {
    try {
      const requestDTO = {
        valorTotal: pagamentoParcelado.valorTotal,
        numeroParcelas: pagamentoParcelado.numeroParcelas,
        dataPrimeiraParcela: pagamentoParcelado.dataPrimeiraParcela,
        descricao: pagamentoParcelado.descricao,
        formaPagamento: PagamentoService.FORMA_PAGAMENTO_MAP[pagamentoParcelado.formaPagamento],
        clienteId: pagamentoParcelado.clienteId,
        status: pagamentoParcelado.status ? PagamentoService.STATUS_MAP[pagamentoParcelado.status] : 'PENDENTE'
      };
      return await this.post('/parcelado', requestDTO);
    } catch (error) {
      console.error('Erro ao criar pagamento parcelado:', error);
      throw error;
    }
  }

  async atualizarPagamento(id, pagamento) {
    try {
      const requestDTO = this.prepareRequestDTO(pagamento);
      await this.put(`/${id}`, requestDTO);
      return true;
    } catch (error) {
      console.error(`Erro ao atualizar pagamento ${id}:`, error);
      throw error;
    }
  }

  async excluirPagamento(id) {
    try {
      await this.delete(`/${id}`);
      return true;
    } catch (error) {
      console.error(`Erro ao excluir pagamento ${id}:`, error);
      throw error;
    }
  }

  async getPagamentosVencidosEProximosAVencer(diasProximosAVencer = 7) {
    try {
      const resultado = await this.get('/status', { diasProximosAVencer });
      return {
        vencidos: resultado.vencidos.map(p => this.prepareResponseDTO(p)),
        proximosAVencer: resultado.proximosAVencer.map(p => this.prepareResponseDTO(p))
      };
    } catch (error) {
      console.error('Erro ao buscar pagamentos vencidos e próximos a vencer:', error);
      return { vencidos: [], proximosAVencer: [] };
    }
  }
}

const pagamentoServiceInstance = new PagamentoService();

const pagamentoService = {
  FORMA_PAGAMENTO_MAP: PagamentoService.FORMA_PAGAMENTO_MAP,
  FORMA_PAGAMENTO_DISPLAY: PagamentoService.FORMA_PAGAMENTO_DISPLAY,
  FORMA_PAGAMENTO_OPTIONS: Object.keys(PagamentoService.FORMA_PAGAMENTO_MAP),

  STATUS_MAP: PagamentoService.STATUS_MAP,
  STATUS_DISPLAY: PagamentoService.STATUS_DISPLAY,
  STATUS_OPTIONS: Object.keys(PagamentoService.STATUS_MAP),

  getPagamentos: () => pagamentoServiceInstance.getPagamentos(),
  getPagamentoPorId: (id) => pagamentoServiceInstance.getPagamentoPorId(id),
  getPagamentosPorCliente: (clienteId) => pagamentoServiceInstance.getPagamentosPorCliente(clienteId),
  getPagamentosPorPeriodo: (dataInicio, dataFim) => pagamentoServiceInstance.getPagamentosPorPeriodo(dataInicio, dataFim),
  getPagamentosVencidosEProximosAVencer: (diasProximosAVencer) => pagamentoServiceInstance.getPagamentosVencidosEProximosAVencer(diasProximosAVencer),
  criarPagamento: (pagamento) => pagamentoServiceInstance.criarPagamento(pagamento),
  criarPagamentoParcelado: (pagamentoParcelado) => pagamentoServiceInstance.criarPagamentoParcelado(pagamentoParcelado),
  atualizarPagamento: (id, pagamento) => pagamentoServiceInstance.atualizarPagamento(id, pagamento),
  excluirPagamento: (id) => pagamentoServiceInstance.excluirPagamento(id)
};

export default pagamentoService;
